<template>
	<AppDialog :dialogName="dialogName" width="620">
		<div v-if="loading">
			<Loader />
			<div>Getting your data from {{ service.service_name }}, this might take a minute</div>
		</div>
		<Col v-else class="justify-between create-sync-group pb-8">
			<div>
				<div class="pt-8">
					<div class="title">
						New synced group -
						<IntegrationIcon
							class="integration-icon"
							:integrationName="service.service_slug"
							:size="serviceIconWidth"
							:padding="serviceIconPadding"
						/>
						{{ serviceName }}
					</div>
				</div>
				<div class="users-and-groups py-2 px-4 mx-6 my-3">
					<div class="display-flex flex-space-between--center py-1">
						<Row class="flex-align-center">
							<IconSuccess /> <span class="pl-2">{{ usersAndGroupsCountText }} found.</span>
						</Row>
						<span @click="seeUsersDetails = !seeUsersDetails" class="see-details">{{ seeUsersDetails ? 'close' : 'see details' }}</span>
					</div>
					<SyncedGroupUsers
						class="synced-data"
						v-if="seeUsersDetails"
						:users="userMatchingUsers"
						:groups="userMatchingGroups"
						:subGroupsText="subGroupsText"
					/>
				</div>
				<div class="options px-4" :class="{ tall: !seeUsersDetails }">
					<ChooseWhatAndHowToSync
						:group="group"
						:service="service"
						:userMatchingData="{ users: userMatchingUsers, groups: userMatchingGroups }"
						@selected-sync-what-type="whatAndHowToSyncTypeSelected"
						@selected-on-delete-type="onTeamDeletionTypeSelected"
						@selected-specific-groups="specificGroupsSelected"
					/>
					<ChooseSyncAction :group="group" :service="service" @selected-action-type="actionTypeSelected" />
				</div>
				<Row class="items-center justify-between mt-6 px-12">
					<h3 class="reg">Workspace role for invited members</h3>
					<RoleSelector :recommendedRole="recommendedRole" :value="localUserRole" :onChange="(role) => (localUserRole = role)" />
				</Row>
			</div>
			<div class="button flex-centerize pt-2">
				<AnemicButton @click="next" v-if="didClickContinueOnce" tile class="px-2 continue-and-invite-button">
					{{ verifyMailInvitationText }}
				</AnemicButton>
				<PrimaryButton v-else :disabled="!canContinue" @click="next">Continue</PrimaryButton>
			</div>
		</Col>
	</AppDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import SyncedGroupUsers from './SyncedGroupUsers';
import AppDialog from '../AppDialog/AppDialog';
import AppDialogManagerService from '../AppDialogManager/app-dialog-manager-service';
import { BILLING_LEVEL_TO_RECOMMENDED_ROLE } from '@/components/Billing/billing-service.const';
import IconSuccess from '@/components/FlowTreeBlock/assets/icon-vi.svg';
import RoleSelector from '@/components/ReactComponentsDataProviders/RoleSelector.vue';
import Loader from '@/components/common/Loader';
import { inviteToAccount } from '@/store/modules/account/actions';
import { teamMembers } from '@/store/modules/account/getters';
import { createGroup, updateGroup } from '@/store/modules/accountGroups/actions';
import { setUserMatchingData } from '@/store/modules/credentials/actions';
import { getAccountConnection } from '@/store/modules/credentials/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { AccountRoles } from '@/utils/account-service.const';
import { SYNC_USERS_ACTION_TYPE } from '@/utils/groups-const';
import GroupsService from '@/utils/groups-service';
import { pluralize } from '@/utils/language-service';
import ChooseSyncAction from '@/views/settings/groups/tabs/SyncedGroupOptions/ChooseSyncAction.vue';
import ChooseWhatAndHowToSync from '@/views/settings/groups/tabs/SyncedGroupOptions/ChooseWhatAndHowToSync.vue';
export default {
	name: 'ChooseSyncedGroupTypeAndUsersDialog',
	components: {
		AppDialog,
		SyncedGroupUsers,
		IconSuccess,
		ChooseWhatAndHowToSync,
		ChooseSyncAction,
		Loader,
		RoleSelector,
	},
	props: {
		service: { type: Object, required: true },
		profileId: { type: [Number, String], required: true },
		isUpdate: { type: Boolean, default: false },
		group: { type: Object, default: null },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGetters([getAccountConnection.fullPath]),
		...mapGettersShorthand({ teamMembers }),
		serviceName() {
			return this.service?.service_name || 'Service';
		},
		connectionName() {
			return this[getAccountConnection.fullPath](this.profileId)?.connection_name;
		},
		newGroupName() {
			const name = `Synced ${this.serviceName} users`;
			if (this.connectionName) {
				return `Synced - ${this.connectionName}`;
			}
			return name;
		},
		updatedGroupName() {
			// change the group name to the new profile connection name if the connection has changed and the current group name is the name we provide,
			//we do not want to change the group name if the user has changed the group name to something of it's own
			if (!this.group.synced.credentials) {
				return this.group.name;
			}
			const oldConnectionName = this[getAccountConnection.fullPath](this.group.synced.credentials)?.connection_name;
			const oldGroupNameWithConnectionByUs = `Synced - ${oldConnectionName}`;
			if (this.profileId && this.profileId !== this.group.synced_credentials && this.group.name === oldGroupNameWithConnectionByUs) {
				return this.newGroupName;
			}
			return this.group.name;
		},
		serviceGroupNoun() {
			return GroupsService.serviceGroupNoun(this.service);
		},
		usersAndGroupsCountText() {
			return `${pluralize('users', this.userMatchingUsers.length, true)} and ${pluralize(
				this.serviceGroupNoun,
				this.userMatchingGroups.length,
				true
			)}`;
		},
		subGroupsText() {
			return `${this.serviceName} ${pluralize(this.serviceGroupNoun, 2)}`;
		},
		canContinue() {
			if (this.group) {
				return this.didChange;
			}
			return true;
		},
		didChange() {
			return (
				!this.didSyncUsersActionChanged &&
				!this.didSyncWhatAndHowToSyncTypeChanged &&
				!this.didOnDeletionTypeChanged &&
				!this.didSpecificGroupChanged
			);
		},
		didSyncUsersActionChanged() {
			return this.selectedActionType !== this.group?.synced.sync_users_action_type;
		},
		didSyncWhatAndHowToSyncTypeChanged() {
			return this.whatAndHowToSyncType !== this.group?.synced.what_to_sync_type;
		},
		didOnDeletionTypeChanged() {
			return this.onDeletionType !== this.group?.synced.on_delete_group_action_type;
		},
		didSpecificGroupChanged() {
			return (
				this.syncSpecificGroups.length !== this.group?.synced.subgroups_to_sync.length ||
				this.syncSpecificGroups.some((x) => !this?.group.synced.subgroups_to_sync.includes(x)) ||
				this.group?.synced.subgroups_to_sync.some((x) => !this.syncSpecificGroups.includes(x))
			);
		},
		verifyMailInvitationText() {
			const amountOfInvitedUsers = this.userMatchingUsers.filter((user) => !this.isUserAlreadyInWorkspace(user)).length;
			return `You are about to send mail invitations to ${pluralize('users', amountOfInvitedUsers, true)}, click here to continue`;
		},
	},
	methods: {
		...mapActions([createGroup.fullPath, updateGroup.fullPath, inviteToAccount.fullPath]),
		...mapActionsShorthand({ setUserMatchingData }),
		isUserAlreadyInWorkspace(user) {
			return this.teamMembers.some((x) => x.email === user.email);
		},
		actionTypeSelected(actionType) {
			this.didClickContinueOnce = false;
			this.selectedActionType = actionType;
		},
		whatAndHowToSyncTypeSelected(howType) {
			this.whatAndHowToSyncType = howType;
		},
		onTeamDeletionTypeSelected(onDeleteType) {
			this.onDeletionType = onDeleteType;
		},
		specificGroupsSelected(syncSpecificGroups) {
			this.syncSpecificGroups = syncSpecificGroups;
		},
		async next() {
			if (this.canContinue) {
				if (this.selectedActionType === SYNC_USERS_ACTION_TYPE.INVITE_NEW && !this.didClickContinueOnce) {
					this.didClickContinueOnce = true;
					return;
				}
				let group = {
					synced: {
						sync_users_action_type: this.selectedActionType,
						what_to_sync_type: this.whatAndHowToSyncType,
						on_delete_group_action_type: this.onDeletionType,
						subgroups_to_sync: this.syncSpecificGroups,
						invite_user_role: this.localUserRole,
						credentials: this.profileId,
						service: this.service.id,
						is_active: true,
					},
				};
				if (this.isUpdate) {
					group = {
						...group,
						id: this.group.id,
						name: this.updatedGroupName,
					};
				} else {
					group = {
						...group,
						name: this.newGroupName,
						type: 'synced',
					};
				}
				const res = await AppDialogManagerService.openGroupPermissionsDialog({
					group,
					isUpdate: this.isUpdate,
					service: this.service,
					profileId: this.profileId,
				});
				this.dialogPromiseHandlers.resolve(res);
				AppDialogManagerService.closeCurrentDialog();
				this.didClickContinueOnce = false;
			}
		},
	},
	async created() {
		try {
			this.loading = true;
			const userMatching = await this.setUserMatchingData({ credentialsId: this.profileId });
			this.userMatchingUsers = userMatching.users;
			this.userMatchingGroups = userMatching.groups;
		} catch (err) {
			this.userMatchingUsers = [];
			this.userMatchingGroups = [];
		} finally {
			this.loading = false;
			if (this.isUpdate && this.group) {
				this.selectedActionType = this.group.synced.sync_users_action_type;
				this.whatAndHowToSyncType = this.group.synced.what_to_sync_type;
				this.onDeletionType = this.group.synced.on_delete_group_action_type;
				this.syncSpecificGroups = this.group.synced.subgroups_to_sync;
			}
			this.recommendedRole = BILLING_LEVEL_TO_RECOMMENDED_ROLE[this.service?.billing_level];
			this.localUserRole = this.recommendedRole || AccountRoles.LiteEndUser;
		}
	},

	data() {
		return {
			loading: false,
			serviceIconWidth: 30,
			serviceIconPadding: 10,
			userMatchingUsers: [],
			userMatchingGroups: [],
			inviteUserList: [],
			dialogName: 'ChooseSyncedGroupTypeAndUsersDialog',
			whatAndHowToSyncType: undefined,
			onDeletionType: undefined,
			syncSpecificGroups: [],
			seeUsersDetails: false,
			didClickContinueOnce: false,
			localUserRole: undefined,
			recommendedRole: undefined,
		};
	},
};
</script>

<style scoped lang="scss">
.create-sync-group {
	height: 690px;
	.title {
		font-weight: 600;
		font-size: 16px;
		display: flex;
		justify-content: flex-start;
		padding-left: 30px;
		.integration-icon {
			padding: 0px 10px;
		}
	}
	.synced-group-users-action-type {
		padding: 0px 30px;
		::v-deep {
			.v-label {
				font-size: small;
				color: var(--primary-font-color) !important;
			}
		}
	}
	.users-and-groups {
		max-height: 160px;
		overflow: auto;
		background: #f0ebeb73;
		border-radius: 9px;
		.synced-data {
			max-height: 110px;
		}
	}
	.options {
		height: 190px;
		overflow: auto;
		&.tall {
			height: 280px;
		}
	}
	.see-details {
		text-decoration: underline;
		cursor: pointer;
	}
}

.continue-and-invite-button {
	color: #5f60dc !important;
	border-radius: 5px !important;
	text-transform: none !important;
	background: #ffffff !important;
	border: 1px solid #5f60dc !important;
}
</style>
